import { useDialogA11y } from "../lib/useDialogA11y";
import { createPortal } from "react-dom";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Users, ArrowRight, X, Bell, CalendarDays } from "lucide-react";
import api, { apiError } from "../lib/apiClient";
import { Editable } from "../components/Editable";
import { ChildCard } from "../components/sizing/ChildCard";
import { SizingDialog } from "../components/sizing/SizingDialog";
import { vocabFor } from "../components/sizing/categories";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { SleepwearStyleField, SLEEPWEAR_LABEL, sleepwearEligible } from "../components/SleepwearStyleField";

// ISO (YYYY-MM-DD) -> display (MM/DD/YYYY)
const isoToDisplay = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
};

// Validate a MM/DD/YYYY string. Returns ISO string if valid & not future, else null.
const parseDob = (display) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(display);
  if (!m) return null;
  const mm = +m[1], dd = +m[2], yyyy = +m[3];
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  if (yyyy < 1900) return null;
  const dt = new Date(yyyy, mm - 1, dd);
  // reject rolled-over impossible dates (e.g. Feb 30)
  if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return null;
  // reject future dates
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (dt > today) return null;
  return `${m[3]}-${m[1]}-${m[2]}`;
};

// ISO -> human age string. Shows months under 2 years, else years.
const ageLabel = (iso) => {
  if (!iso) return "";
  const b = new Date(iso + "T00:00:00");
  const now = new Date();
  let months = (now.getFullYear() - b.getFullYear()) * 12 + (now.getMonth() - b.getMonth());
  if (now.getDate() < b.getDate()) months -= 1;
  if (months < 0) return "";
  if (months < 24) return `${months} ${months === 1 ? "month" : "months"} old`;
  const years = Math.floor(months / 12);
  return `${years} ${years === 1 ? "year" : "years"} old`;
};

function DobField({ value, onChange, testid = "wizard-birthday" }) {
  const [display, setDisplay] = React.useState(isoToDisplay(value));
  const [error, setError] = React.useState("");
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => { setDisplay(isoToDisplay(value)); }, [value]);

  const handleType = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setDisplay(out);
    if (out.length === 10) {
      const iso = parseDob(out);
      if (iso) { setError(""); onChange(iso); }
      else { setError("Enter a real date that isn't in the future."); onChange(""); }
    } else {
      setError("");
      if (value) onChange("");
    }
  };

  const handleBlur = () => {
    if (display && display.length > 0 && display.length < 10) setError("Use MM/DD/YYYY.");
  };

  const pickDate = (d) => {
    if (!d) return;
    const y = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const iso = `${y}-${mm}-${dd}`;
    setDisplay(`${mm}/${dd}/${y}`);
    setError("");
    onChange(iso);
    setOpen(false);
  };

  const age = value ? ageLabel(value) : "";

  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-ink-muted">Date of birth</label>
      <div className="flex gap-2 items-stretch mt-1">
        <input
          data-testid={testid}
          value={display}
          onChange={(e) => handleType(e.target.value)}
          onBlur={handleBlur}
          aria-label="Date of birth (MM/DD/YYYY)" placeholder="MM/DD/YYYY"
          inputMode="numeric"
          maxLength={10}
          className="input flex-1 !mt-0"
          aria-invalid={!!error}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="wizard-birthday-calendar-btn"
              className="shrink-0 h-12 w-12 rounded-2xl border border-line bg-white flex items-center justify-center text-ink-soft hover:bg-cream transition-colors"
              aria-label="Open calendar"
            >
              <CalendarDays size={18} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              captionLayout="dropdown"
              fromYear={1900}
              toYear={new Date().getFullYear()}
              selected={value ? new Date(value + "T00:00:00") : undefined}
              defaultMonth={value ? new Date(value + "T00:00:00") : new Date()}
              disabled={{ after: new Date() }}
              onSelect={pickDate}
            />
          </PopoverContent>
        </Popover>
      </div>
      {error ? (
        <p data-testid="wizard-birthday-error" className="text-xs text-red-500 mt-1 font-medium">{error}</p>
      ) : age ? (
        <p data-testid="wizard-birthday-age" className="text-xs text-brand mt-1 font-semibold">Age: {age}</p>
      ) : null}
    </div>
  );
}

const TYPES = [
  { v: "boy", label: "Boy" },
  { v: "girl", label: "Girl" },
  { v: "teen_boy", label: "Teen boy" },
  { v: "teen_girl", label: "Teen girl" },
  { v: "baby_boy", label: "Baby boy" },
  { v: "baby_girl", label: "Baby girl" },
];
const FITS = [
  { v: "fitted", label: "Fitted" },
  { v: "regular", label: "Regular" },
  { v: "roomy", label: "Roomy" },
];

// Age/gender-appropriate PUBLIC labels for the canonical internal clothing-type keys.
// Canonical keys (tops|bottoms|dresses|pajamas|outerwear) are preserved for backward
// compatibility; only the visible label + which keys appear change per gender.
// Shoes are intentionally hidden everywhere for now (no shoe-shopping flow yet).
// Child-specific garment vocabulary (boys / girls / babies) from the shared categories module.
function clothingOptionsFor(childType) {
  return vocabFor(childType);
}

const emptyDraft = () => ({
  id: crypto.randomUUID(), nickname: "", type: "boy", birthday: "",
  brand_sizes: [], fit_preference: "regular", height: "", weight: "",
  sleepwear_style_preference: "either", sleepwear_pref_migrated: false, sleepwear_pref_confirmed: false,
  parent_measurements: { chest_in: "", waist_in: "", inseam_in: "" },
});

export default function FamilyProfile() {
  const navigate = useNavigate();
  const [fam, setFam] = useState({ family_name: "", location: "Monsey, NY", children: [] });
  const [reminders, setReminders] = useState([]);
  const [brands, setBrands] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [profileEdit, setProfileEdit] = useState(null); // { draft, isNew }
  const [sizingChild, setSizingChild] = useState(null);
  const [savingFam, setSavingFam] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = () => {
    api.get("/family").then(({ data }) => { if (data) setFam(data); }).catch(() => {});
    api.get("/family/sizing-reminders").then(({ data }) => setReminders(data || [])).catch(() => {});
  };
  useEffect(() => {
    load();
    // One readiness source for every brand list: canonical lookup availability (reference tier) — never the legacy workbook.
    api.get("/sizing-canonical/lookup/brands").then(({ data }) => {
      const list = data.brands || [];
      setBrands(list.map((b) => b.brand));
      setAvailability(Object.fromEntries(list.map((b) => [b.brand, !!b.available])));
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = async (children) => {
    setSavingFam(true);
    try {
      const { data } = await api.post("/family", { ...fam, children });
      setFam(data);
      api.get("/family/sizing-reminders").then((r) => setReminders(r.data || [])).catch(() => {});
      return data;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      return null;
    } finally {
      setSavingFam(false);
    }
  };

  const removeChild = async (id) => {
    const next = fam.children.filter((c) => c.id !== id);
    if (await persist(next)) toast.success("Child removed");
  };
  const startAdd = () => setProfileEdit({ draft: emptyDraft(), isNew: true });
  const startEditProfile = (c) => setProfileEdit({ draft: { ...emptyDraft(), ...c }, isNew: false });
  const saveFamilyMeta = async () => { if (await persist(fam.children)) toast.success("Saved"); };

  return (
    <div className="max-w-2xl mx-auto space-y-6 fade-up">
      <div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink flex items-center gap-2"><Users className="text-brand" /> <Editable id="profile.title">Family Profile</Editable></h1>
        <p className="text-ink-soft mt-1"><Editable id="profile.subtitle">Save optional measurements and notes about sizes worn today. Browse official brand charts and choose each size yourself.</Editable></p>
      </div>

      {reminders.length > 0 && (
        <div className="space-y-2" data-testid="sizing-reminders">
          {reminders.map((r) => (
            <div key={r.child_id} data-testid={`sizing-reminder-${r.child_id}`} className="bg-gold/10 border border-gold/30 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-ink font-semibold flex items-center gap-2"><Bell size={16} className="text-gold-text shrink-0" /> {r.message}</p>
              <button onClick={() => { const c = fam.children.find((x) => x.id === r.child_id); if (c) setSizingChild(c); }} data-testid={`review-sizing-${r.child_id}`} className="min-h-[44px] px-4 rounded-full bg-gold text-white font-bold text-sm hover:bg-gold-hover transition-all shrink-0">Update sizing</button>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-3xl border border-brand/15 shadow-soft p-5 grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="family-name-input" className="text-sm font-bold text-ink">Family label (optional)</label>
          <input id="family-name-input" data-testid="family-name-input" value={fam.family_name || ""} onChange={(e) => setFam({ ...fam, family_name: e.target.value })} placeholder="The Gold Family" className="mt-1.5 w-full h-12 px-4 rounded-2xl border-2 border-line bg-cream focus:border-brand outline-none" />
        </div>
        <div>
          <label htmlFor="family-location-input" className="text-sm font-bold text-ink">Location</label>
          <input id="family-location-input" data-testid="family-location-input" value={fam.location || ""} onChange={(e) => setFam({ ...fam, location: e.target.value })} className="mt-1.5 w-full h-12 px-4 rounded-2xl border-2 border-line bg-cream focus:border-brand outline-none" />
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <button onClick={saveFamilyMeta} disabled={savingFam} data-testid="save-family-btn" className="min-h-[44px] px-5 rounded-full border-2 border-line text-ink-soft font-bold hover:border-brand/30 transition-all disabled:opacity-60">{savingFam ? "Saving…" : "Save family details"}</button>
        </div>
      </div>

      <div className="space-y-4">
        {fam.children.map((c, i) => (
          <ChildCard key={c.id} child={c} idx={i} availability={availability} refreshKey={refreshKey} onEditProfile={startEditProfile} onEditSizing={setSizingChild} onRemove={removeChild} onChanged={() => { load(); setRefreshKey((k) => k + 1); }} />
        ))}
      </div>

      <button onClick={startAdd} data-testid="add-child-btn" className="w-full h-14 rounded-3xl border-2 border-brand/50 text-brand font-bold flex items-center justify-center gap-2 hover:bg-brand-light transition-all">
        <Plus size={20} /> <Editable id="profile.add-child">Add a child</Editable>
      </button>
      {fam.children.length > 0 && (
        <button onClick={() => navigate("/family-deals")} data-testid="view-matching-sets" className="w-full h-14 rounded-3xl bg-brand text-white font-bold hover:bg-brand-hover transition-all flex items-center justify-center gap-2">View matching sets <ArrowRight size={18} /></button>
      )}

      {profileEdit && (
        <ProfileDialog
          draft={profileEdit.draft} isNew={profileEdit.isNew} onClose={() => setProfileEdit(null)}
          onDone={async (child) => {
            const exists = fam.children.some((c) => c.id === child.id);
            const next = exists ? fam.children.map((c) => (c.id === child.id ? { ...c, ...child } : c)) : [...fam.children, child];
            const saved = await persist(next);
            if (saved) { setProfileEdit(null); toast.success(exists ? "Profile saved" : "Child added — now add what you know about sizing"); if (!exists) { const nc = saved.children.find((c) => c.id === child.id); if (nc) setSizingChild(nc); } }
          }}
        />
      )}
      {sizingChild && (
        <SizingDialog child={fam.children.find((c) => c.id === sizingChild.id) || sizingChild} brands={brands} availability={availability} ctOptions={clothingOptionsFor(sizingChild.type)} onChanged={() => { load(); setRefreshKey((k) => k + 1); }} onClose={() => setSizingChild(null)} onSaved={(data) => { setFam(data); setSizingChild(null); load(); setRefreshKey((k) => k + 1); }} />
      )}
      <style>{`.input{margin-top:.25rem;width:100%;height:2.75rem;padding:0 .9rem;border-radius:1rem;border:2px solid #F4E3E6;background:#FFF9FA;outline:none}.input:focus{border-color:#FF4D6D}`}</style>
    </div>
  );
}

// "About this child": nickname, type, date of birth, preferred fit, sleepwear preference. Sizing lives in SizingDialog.
function ProfileDialog({ draft: initial, isNew, onDone, onClose }) {
  const [draft, setDraft] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const set = (patch) => setDraft({ ...draft, ...patch });
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const close = () => { if (!dirty || window.confirm("Discard unsaved changes?")) onClose(); };
  const ref = useDialogA11y(true, close);
  const submit = async () => {
    if (!draft.nickname.trim()) { toast.error("Enter a nickname."); return; }
    if (!draft.birthday) { toast.error("Enter the date of birth."); return; }
    setSubmitting(true);
    try { await onDone(draft); } finally { setSubmitting(false); }
  };
  return createPortal(
    <div className="fixed inset-0 h-[100dvh] z-[120] bg-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={close}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby="child-wizard-title" tabIndex={-1} className="bg-white rounded-t-3xl sm:rounded-3xl shadow-lift w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[92vh] overflow-y-auto p-6 fade-up outline-none" onClick={(e) => e.stopPropagation()} data-testid="child-wizard">
        <div className="flex items-center justify-between mb-4">
          <h3 id="child-wizard-title" className="font-heading font-black text-xl text-ink">{isNew ? "About this child" : `${initial.nickname || "Child"}'s profile`}</h3>
          <button onClick={close} data-testid="wizard-close" aria-label="Close" className="h-11 w-11 rounded-full text-ink-muted hover:bg-cream flex items-center justify-center"><X size={20} /></button>
        </div>
        <div className="space-y-3" data-testid="wizard-step-1">
          <div>
            <label htmlFor="wizard-nickname" className="text-xs font-bold uppercase tracking-wider text-ink-muted">Nickname</label>
            <input data-testid="wizard-nickname" id="wizard-nickname" autoComplete="off" required value={draft.nickname} onChange={(e) => set({ nickname: e.target.value })} placeholder="Chani" className="input" />
          </div>
          <div>
            <label htmlFor="wizard-type" className="text-xs font-bold uppercase tracking-wider text-ink-muted">Gender / type</label>
            <select id="wizard-type" data-testid="wizard-type" value={draft.type} onChange={(e) => set({ type: e.target.value })} className="input">
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </div>
          <DobField testid="wizard-birthday" value={draft.birthday || ""} onChange={(iso) => set({ birthday: iso })} />
          <div data-testid="wizard-preferred-fit-block">
            <p id="preferred-fit-label" className="text-xs font-bold uppercase tracking-wider text-ink-muted">How would you like this child's clothes to fit?</p>
            <div role="radiogroup" aria-labelledby="preferred-fit-label" className="grid grid-cols-3 gap-2 mt-1.5" data-testid="wizard-preferred-fit">
              {FITS.map((f) => {
                const active = (draft.fit_preference || "regular") === f.v;
                return <button key={f.v} type="button" role="radio" aria-checked={active} data-testid={`preferred-fit-${f.v}`} onClick={() => set({ fit_preference: f.v })} className={`h-11 rounded-xl border-2 text-sm font-bold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 ${active ? "border-brand bg-brand text-white" : "border-line bg-white text-ink-soft hover:border-brand/40"}`}>{active ? "✓ " : ""}{f.label}</button>;
              })}
            </div>
            <p className="text-xs text-ink-muted mt-1.5" data-testid="preferred-fit-help">Your preference — separate from a brand's published Regular / Slim / Husky / Plus.</p>
          </div>
          {sleepwearEligible(draft) && <SleepwearStyleField value={draft.sleepwear_style_preference} onChange={(v) => set({ sleepwear_style_preference: v })} migrated={!!draft.sleepwear_pref_migrated && !draft.sleepwear_pref_confirmed} />}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={close} data-testid="wizard-cancel" className="h-12 px-5 rounded-full border-2 border-line text-ink-soft font-bold">Cancel</button>
            <button type="button" onClick={submit} disabled={submitting} data-testid="wizard-next" className="flex-1 h-12 rounded-full bg-brand text-white font-bold hover:bg-brand-hover transition-all disabled:opacity-60">{submitting ? "Saving…" : isNew ? "Save & add sizing" : "Save profile"}</button>
          </div>
        </div>
      </div>
    </div>
  , document.body);
}
